import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Shared Confirmation Modal Component
 *
 * A reusable confirmation dialog for destructive or important actions.
 * Supports customizable title, message, button labels, and variant colors.
 *
 * Usage:
 * ```html
 * <app-confirmation-modal
 *   *ngIf="showConfirmModal"
 *   title="Archive Organization"
 *   message="Are you sure you want to archive this organization?"
 *   confirmLabel="Archive"
 *   variant="warning"
 *   [processing]="archiving"
 *   (confirmed)="onConfirm()"
 *   (cancelled)="showConfirmModal = false">
 * </app-confirmation-modal>
 * ```
 */
@Component({
  selector: 'app-confirmation-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onCancel()"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i *ngIf="icon" [class]="icon + ' me-2'"></i>{{ title }}
            </h5>
            <button type="button" class="btn-close" (click)="onCancel()" [disabled]="processing" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">{{ message }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="processing">
              {{ cancelLabel }}
            </button>
            <button type="button" [class]="'btn btn-' + variant" (click)="onConfirm()" [disabled]="processing">
              <span *ngIf="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ processing ? processingLabel : confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class ConfirmationModalComponent {
  @Input() title = 'Confirm Action';
  @Input() message = 'Are you sure you want to proceed?';
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';
  @Input() processingLabel = 'Processing...';
  @Input() variant: 'warning' | 'danger' | 'primary' = 'warning';
  @Input() icon = '';
  @Input() processing = false;

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  onConfirm(): void {
    this.confirmed.emit();
  }

  onCancel(): void {
    if (!this.processing) {
      this.cancelled.emit();
    }
  }
}
